import React from 'react';
import { Route, Switch, useParams } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import * as _ from 'lodash';

import paths from '../../constants/routes.json';
import { ListingDetails_ListingData } from '../../types/graph/ListingDetails_ListingData';
import Header from './Details/Header';
import Property from './Details/Property';
import Contacts from './Details/Contacts';
import Website from './Details/Website';
import Streams from './Details/Streams';
import Sharing from './Details/Sharing';
import Marketing from './Details/Marketing';
import Advanced from './Details/Advanced';
import Analytics from './Details/Analytics';

interface ListingDetailsParams {
  listingId: string;
}

const ListingDetails: React.FC = () => {
  const { listingId } = useParams<ListingDetailsParams>();

  const { data, loading, error } = useQuery<any>(LISTING_QUERY, {
    variables: { listingId },
  });

  const [deleteListing] = useMutation(LISTING_DELETE);

  const onListingDelete = (listingId: string) => {
    deleteListing({ variables: { listingId } });
  };

  if (error) {
    console.error(error);
  }

  if (loading || !data || !data.listing) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div>
        <Header listing={data.listing} onListingDelete={onListingDelete} />
      </div>
      <div className="mt-4">
        <Switch>
          <Route path={`${paths.LISTINGS}/:listingId/property`}>
            <Property listing={data.listing} />
          </Route>
          <Route path={`${paths.LISTINGS}/:listingId/contacts`}>
            <Contacts listing={data.listing} />
          </Route>
          <Route path={`${paths.LISTINGS}/:listingId/website`}>
            <Website listing={data.listing} />
          </Route>
          <Route path={`${paths.LISTINGS}/:listingId/marketing`}>
            <Marketing listing={data.listing} />
          </Route>
          <Route path={`${paths.LISTINGS}/:listingId/streams`}>
            <Streams />
          </Route>
          <Route path={`${paths.LISTINGS}/:listingId/analytics`}>
            <Analytics listing={data.listing} />
          </Route>
          <Route path={`${paths.LISTINGS}/:listingId/sharing`}>
            <Sharing />
          </Route>
          <Route path={`${paths.LISTINGS}/:listingId/advanced`}>
            <Advanced listing={data.listing} />
          </Route>
        </Switch>
      </div>
    </div>
  );
};

export default ListingDetails;

export const LISTING_QUERY = gql`
  query ListingDetails_ListingData($listingId: String!) {
    listing(listingId: $listingId) {
      id
      sid
      status
      hasAnalytics
      analyticsUrl
      property {
        id
        address {
          street
          city
          region
          postal
          country
          lat
          lng
        }
        media {
          sid
          filePath
          originalFileName
          type
          context
          embedProvider
          embedId
          embedUrl
          embedType
        }
        prices {
          amount
          setAt
        }
        bedrooms
        bathrooms
        dimensions
        year
        description
        hasGarage
        hasFireplace
        hasPool
        hasAC
      }
      website {
        styling {
          template
          colors {
            primary
            accent
          }
        }
        domains {
          name
          expiresAt
          enabled
        }
      }
      contacts {
        id
        person {
          name {
            first
            last
            full
          }
          email
          phones {
            number
            type
          }
          socialLinks {
            url
            type
          }
          avatar {
            filePath
          }
        }
        company {
          name
          title
          socialLinks {
            url
            type
          }
          logo {
            filePath
          }
        }
      }
      marketing {
        facebook {
          action
          type
          announcement
          postId
          pageId
          pagePostId
          pageName
          createdAt
        }
      }
    }
  }
`;

const LISTING_DELETE = gql`
  mutation ListingDetails_ListingDeleteData($listingId: String!) {
    listingDelete(listingId: $listingId) {
      id
    }
  }
`;
